import settings from 'popups/Settings'
import { createTodoFolder, isTodoFolder, TodoFolder } from './TodoFolder'
import {
  deepEqual,
  getSharedAccountWithFolders,
  TodoUserInfo,
  removeTodoItems,
} from './TodoService'

import { reloadTodos, todoFolderEmitter } from './TodoSync'
import { getTodoDB, isTodoDBEnabled, isTodoDBReady } from './todoDB'
import { initializeTodoDBSystem } from './todoDB/TodoDBService'

export const setAccount = (info: TodoUserInfo) => {
  getSharedAccountWithFolders().userInfo = info
}

export const getUserInfo = (): TodoUserInfo => {
  return getSharedAccountWithFolders().userInfo
}

/**
 * Todo 폴더 목록 설정 및 동기화 관리
 *
 * @param info - 사용자 정보 (이름, 이메일)
 * @param folderList - 서버에서 받은 폴더 목록
 * @param initSync - 초기 동기화 여부 (Calendar 초기화 시 true)
 * @returns Promise<boolean> - 변경사항 발생 여부
 *
 * 주요 기능:
 * 1. 폴더 타입 검증 및 기본 폴더 설정
 * 2. 동기화 설정 관리 (settings.todoSyncTargets)
 * 3. DB 초기화 및 폴더 정보 저장
 * 4. UI 업데이트 트리거
 */
export const setFolders = async (
  info: TodoUserInfo,
  folderList: TodoFolder[],
  initSync = false,
): Promise<boolean> => {
  // ===== 1단계: 폴더 타입 검증 및 기본 폴더 설정 =====
  // 유효한 Todo 폴더만 필터링하고 표준 형식으로 변환
  let validFolders = folderList.filter(isTodoFolder).map((folder) => createTodoFolder(folder, info))

  // 기본 폴더(isDefault) 설정 로직
  if (!validFolders.some((f) => f.isDefault)) {
    // 우선순위 1: 'Tasks' 이름을 가진 폴더를 기본 폴더로 설정
    const tasksFolder = validFolders.find((f) => f.displayName === 'Tasks')
    if (tasksFolder) {
      tasksFolder.isDefault = true
    } else if (validFolders.length > 0) {
      // 우선순위 2: 첫 번째 폴더를 기본 폴더로 설정
      validFolders[0].isDefault = true
    }
  }

  // 사용자 정보 정규화 (필요한 필드만 추출)
  const newUserInfo = {
    name: info.name,
    email: info.email,
  }

  // ===== 2단계: 동기화 설정 관리 =====
  const todoSettings = settings.getValue('todo') || {}

  // 최초 설정 여부 확인 (todoSyncTargets 배열이 없으면 최초 설정)
  const isFirstFetch = !Array.isArray(todoSettings.todoSyncTargets)

  // 기존 동기화 대상 폴더 목록 가져오기
  const syncTargets = Array.isArray(todoSettings.todoSyncTargets)
    ? todoSettings.todoSyncTargets
    : []

  if (initSync) {
    // 초기 동기화 모드: Calendar 초기화 시 호출
    validFolders.forEach((folder) => {
      if (isFirstFetch) {
        // 최초 설정: 기본 폴더만 동기화 활성화 (Calendar 패턴과 동일)
        folder.isSync = folder.isDefault
      } else {
        // 기존 설정 유지: 이전 동기화 설정 상태 복원
        folder.isSync = syncTargets.includes(folder.url)
      }
    })

    // 최초 설정시에만 기본 폴더를 동기화 대상으로 settings에 저장
    if (isFirstFetch) {
      const syncFolderUrls = validFolders.filter((f) => f.isSync).map((f) => f.url)
      todoSettings.todoSyncTargets = syncFolderUrls
      settings
        .setValue('todo', todoSettings)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { todo: todoSettings },
          })
        })
        .catch((err) => console.error('Failed to sync todo settings:', err))
    }
  } else {
    // 일반 폴더 새로고침 모드: 기존 동기화 설정 상태 유지
    validFolders.forEach((folder) => {
      folder.isSync = syncTargets.includes(folder.url)
    })
  }

  // ===== 3단계: 변경사항 감지 =====
  const accountWithFolders = getSharedAccountWithFolders()
  const userInfoChanged = !deepEqual(accountWithFolders.userInfo, newUserInfo)
  const foldersChanged = !deepEqual(accountWithFolders.folders, validFolders)
  const hasChanges = userInfoChanged || foldersChanged

  // ===== 4단계: DB 초기화 및 상태 확인 =====
  const isDBEnabled = isTodoDBEnabled()
  let isDBReady = isTodoDBReady()

  // initSync=true이고 DB가 준비되지 않은 경우 먼저 DB 초기화
  if (initSync && isDBEnabled && !isDBReady) {
    try {
      await initializeTodoDBSystem()
      isDBReady = isTodoDBReady()
      console.log('DB initialized for folder setup, ready:', isDBReady)
    } catch (error) {
      console.error('Failed to initialize DB for folder setup:', error)
    }
  }

  // DB 업데이트 필요 여부 판단
  let shouldUpdateDB = false
  if (isDBEnabled && isDBReady) {
    try {
      const todoDB = getTodoDB()
      const existingFolders = await todoDB.getAllFolders()

      // DB 업데이트가 필요한 조건들
      const isDBEmpty = existingFolders.length === 0 // DB에 폴더가 없음

      // URL 기반으로 폴더 변경 감지 개선
      const existingUrls = new Set(existingFolders.map((f) => f.url))
      const newUrls = new Set(validFolders.map((f) => f.url))
      const hasUrlChanges =
        existingUrls.size !== newUrls.size ||
        !Array.from(existingUrls).every((url) => newUrls.has(url))

      // 개별 폴더 속성 변경 감지
      const hasFolderPropertyChanges = validFolders.some((newFolder) => {
        const existingFolder = existingFolders.find((f) => f.url === newFolder.url)
        return !existingFolder || !deepEqual(existingFolder, newFolder)
      })

      const hasFolderChanges = hasUrlChanges || hasFolderPropertyChanges

      shouldUpdateDB = isDBEmpty || hasFolderChanges || hasChanges || initSync

      console.log(
        `DB folder check: empty=${isDBEmpty}, urlChanges=${hasUrlChanges}, propertyChanges=${hasFolderPropertyChanges}, shouldUpdate=${shouldUpdateDB}`,
      )
    } catch (error) {
      console.error('Failed to check existing folders in DB:', error)
      shouldUpdateDB = true // DB 오류 시 업데이트 시도
    }
  }

  // ===== 5단계: 메모리 상태 및 DB 업데이트 =====
  if (hasChanges || initSync || shouldUpdateDB) {
    // 메모리 상태 업데이트
    accountWithFolders.userInfo = newUserInfo

    // 기존 폴더들의 isSync 상태 보존하면서 폴더 목록 업데이트
    if (!initSync) {
      // 일반 새로고침: 기존 폴더들의 isSync 상태를 validFolders에 반영
      const existingFolderMap = new Map(accountWithFolders.folders.map((f) => [f.url, f]))
      validFolders.forEach((folder) => {
        const existingFolder = existingFolderMap.get(folder.url)
        if (existingFolder) {
          // 기존 폴더의 isSync 상태 유지 (updateFolderSync에서 변경한 값)
          folder.isSync = existingFolder.isSync
        }
      })
    }

    // 메모리 캐시 정리: 삭제된 폴더의 todos 제거
    try {
      const prevFolderUrls = new Set(accountWithFolders.folders.map((f) => f.url))
      const newFolderUrls = new Set(validFolders.map((f) => f.url))
      for (const url of prevFolderUrls) {
        if (!newFolderUrls.has(url)) {
          await removeTodoItems(url)
          console.log(`[setFolders] Cleaned memory cache for deleted folder: ${url}`)
        }
      }
    } catch (cleanupErr) {
      console.error('[setFolders] Failed cleaning memory cache for deleted folders:', cleanupErr)
    }

    accountWithFolders.folders = validFolders

    // DB 연동이 활성화된 경우 폴더 정보를 DB에 저장
    if (isDBEnabled && isDBReady && shouldUpdateDB) {
      try {
        const todoDB = getTodoDB()

        // 모든 폴더를 DB에 일괄 교체 (삭제된 폴더도 정리됨)
        await todoDB.replaceAllFolders(validFolders)

        // 참고: 동기화 폴더 목록은 settings.todoSyncTargets에서 관리됨 (중복 저장 불필요)
        console.log(`Replaced all folders in DB with ${validFolders.length} folders`)
      } catch (error) {
        console.error('Failed to replace folders in DB:', error)
      }
    }

    // ===== 6단계: UI 업데이트 트리거 =====
    if (initSync) {
      // 초기 동기화 시에만 Todo 항목 다시 로드 및 UI 업데이트
      reloadTodos() // Todo 항목 동기화 시작\
    }

    todoFolderEmitter.emit('folder') // 폴더 목록 UI 업데이트
  }

  return hasChanges
}

export const getFolders = () => {
  return getSharedAccountWithFolders().folders
}

export const getSyncedFolders = () => {
  return getSharedAccountWithFolders().folders.filter((folder) => folder.isSync)
}

export const getSortedFolders = () => {
  // 메모리 캐시에서 조회 및 정렬 (동기 버전 - 기존 호출처 호환)
  const sortedFolderList = getSharedAccountWithFolders()
    .folders.slice()
    .sort((a, b) => {
      // isDefault가 true인 폴더가 먼저
      if (a.isDefault && !b.isDefault) return -1
      if (!a.isDefault && b.isDefault) return 1

      // isShared가 false인 폴더가 먼저 (개인 폴더 우선)
      if (!a.isShared && b.isShared) return -1
      if (a.isShared && !b.isShared) return 1

      // 같은 그룹 내에서 displayName 오름차순
      return (a.displayName || '').localeCompare(b.displayName || '')
    })
  return sortedFolderList
}

export const getSortedFoldersAsync = async (): Promise<TodoFolder[]> => {
  // DB 연동이 활성화된 경우 DB에서 조회
  if (isTodoDBEnabled() && isTodoDBReady()) {
    try {
      const todoDB = getTodoDB()
      return await todoDB.getFoldersSorted()
    } catch (error) {
      console.error('[getSortedFoldersAsync] Failed to get sorted folders from DB:', error)
      // DB 조회 실패 시 메모리 캐시 사용
    }
  }

  // 메모리 캐시에서 조회 및 정렬 (fallback)
  return getSortedFolders()
}

export const removeFolder = (url: string) => {
  const accountWithFolders = getSharedAccountWithFolders()
  accountWithFolders.folders = accountWithFolders.folders.filter((folder) => folder.url !== url)
}

export const updateFolderSync = async (folderUrl: string, isSync: boolean): Promise<void> => {
  console.log('[updateFolderSync] Starting:', folderUrl, isSync)

  // === 1. 폴더 동기화 상태 업데이트 ===
  // accountWithFolders.folders에서 해당 폴더의 isSync 속성을 변경
  // 이는 TodoNavigationDrawer UI에서 동기화 토글 상태를 즉시 반영하기 위함
  const todoSettings = settings.getValue('todo') || {}
  const currentSyncTargets = Array.isArray(todoSettings.todoSyncTargets)
    ? todoSettings.todoSyncTargets
    : []

  const accountWithFolders = getSharedAccountWithFolders()
  const folder = accountWithFolders.folders.find((f) => f.url === folderUrl)
  if (folder) {
    folder.isSync = isSync
    console.log(
      '[updateFolderSync] Updated folder.isSync in accountWithFolders:',
      folderUrl,
      isSync,
    )

    // === 2. DB에 폴더 상태 저장 ===
    // DB 연동이 활성화된 경우 폴더 정보를 DB에 저장하여
    // 브라우저 재시작 후에도 동기화 상태가 유지되도록 함
    if (isTodoDBEnabled() && isTodoDBReady()) {
      try {
        const todoDB = getTodoDB()
        await todoDB.saveFolder(folder)
        console.log('[updateFolderSync] Folder saved to DB:', folderUrl)
      } catch (error) {
        console.error('Failed to update folder in DB:', error)
      }
    }
  }

  // === 3. Settings의 todoSyncTargets 배열 업데이트 ===
  // 시퀀스 다이어그램 14.4에 따라 Settings에서 동기화 대상 폴더 목록을 관리
  // 이 배열은 실제 동기화 작업에서 사용되며, getSyncFolders() 등에서 참조됨
  if (isSync) {
    // === 3a. 동기화 활성화 ===
    // folderUrl을 todoSyncTargets에 추가 (중복 방지)
    if (!currentSyncTargets.includes(folderUrl)) {
      todoSettings.todoSyncTargets = [...currentSyncTargets, folderUrl]
      settings
        .setValue('todo', todoSettings)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { todo: todoSettings },
          })
        })
        .catch((err) => console.error('Failed to sync todo settings:', err))
      console.log('[updateFolderSync] Added to sync targets')
    } else {
      console.log('[updateFolderSync] Folder already in sync targets:', folderUrl)
    }
  } else {
    // === 3b. 동기화 비활성화 ===
    // folderUrl을 todoSyncTargets에서 제거
    const updatedSyncTargets = currentSyncTargets.filter((url: string) => url !== folderUrl)
    todoSettings.todoSyncTargets = updatedSyncTargets
    settings
      .setValue('todo', todoSettings)
      .then(() => {
        settings.getStore().dispatch({
          type: 'settings/UPLOAD_SETTINGS',
          payload: { todo: todoSettings },
        })
      })
      .catch((err) => console.error('Failed to sync todo settings:', err))
    console.log('[updateFolderSync] Removed from sync targets')

    // === 3c. 모든 동기화 설정이 꺼진 경우 DB의 모든 todos 삭제 ===
    if (updatedSyncTargets.length === 0 && isTodoDBEnabled() && isTodoDBReady()) {
      try {
        const todoDB = getTodoDB()
        await todoDB.clearAllTodos()
        console.log('[updateFolderSync] All todos cleared from DB as no folders are synced')

        // 메모리 캐시도 모두 정리
        const { removeTodoItems } = await import('./TodoService')
        for (const url of currentSyncTargets) {
          await removeTodoItems(url)
        }
        console.log('[updateFolderSync] All todo memory cache cleared as no folders are synced')
      } catch (error) {
        console.error(
          '[updateFolderSync] Failed to clear all todos when no folders are synced:',
          error,
        )
      }
    }
  }

  // === 4. UI 업데이트 이벤트 ===
  // 시퀀스 다이어그램 14.4에 따라 폴더 상태 변경 후 UI 업데이트 이벤트 발생
  try {
    const { todoFolderEmitter } = await import('./TodoSync')
    todoFolderEmitter.emit('folder')
    console.log('[updateFolderSync] todoFolderEmitter.emit("folder") triggered')
  } catch (error) {
    console.error('[updateFolderSync] Failed to emit folder event:', error)
  }

  console.log('[updateFolderSync] Completed:', folderUrl, isSync)
}

export const getSyncFolders = async (): Promise<TodoFolder[]> => {
  // DB 연동이 활성화된 경우 DB에서 조회
  if (isTodoDBEnabled() && isTodoDBReady()) {
    try {
      const todoDB = getTodoDB()
      return await todoDB.getSyncFolders()
    } catch (error) {
      console.error('[getSyncFolders] Failed to get sync folders from DB:', error)
      // DB 조회 실패 시 메모리 캐시 사용
    }
  }

  // 메모리 캐시에서 조회 (기존 로직)
  const todoSettings = settings.getValue('todo') || {}
  const syncTargets: string[] = Array.isArray(todoSettings.todoSyncTargets)
    ? todoSettings.todoSyncTargets
    : []
  const folders: TodoFolder[] = getFolders()
  return folders.filter((folder) => syncTargets.includes(folder.url))
}

// Cache functions removed - IndexedDB handles caching natively
